import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const STEPS = [
  { id: 1, title: 'Company Info', icon: 'Building2' },
  { id: 2, title: 'Plan Selection', icon: 'CreditCard' },
  { id: 3, title: 'Payment Setup', icon: 'DollarSign' },
  { id: 4, title: 'Create Admin User', icon: 'UserPlus' },
];

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    pricePerUser: 19,
    maxUsers: 5,
    color: 'border-gray-200',
    badge: '',
    features: ['Up to 5 users', 'Ticket & job management', 'Mobile technician app', 'Basic reporting', 'Email support'],
  },
  {
    id: 'professional',
    name: 'Professional',
    pricePerUser: 29,
    maxUsers: 25,
    color: 'border-primary',
    badge: 'Most Popular',
    features: ['Up to 25 users', 'Everything in Starter', 'GPS location tracking', 'Signature capture', 'Invoice & quote management', 'Customer portal', 'Priority support'],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    pricePerUser: 49,
    maxUsers: 999,
    color: 'border-purple-300',
    badge: 'Unlimited',
    features: ['Unlimited users', 'Everything in Professional', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee', 'Custom reporting', '24/7 phone support'],
  },
];

const INDUSTRIES = ['Oil & Gas', 'HVAC', 'Plumbing', 'Electrical', 'Construction', 'Landscaping', 'Cleaning Services', 'IT Services', 'Other'];
const COMPANY_SIZES = ['1-5 employees', '6-15 employees', '16-50 employees', '51-200 employees', '200+ employees'];

const OrganizationOnboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [companyInfo, setCompanyInfo] = useState({
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyZip: '',
    industry: '',
    companySize: '',
  });

  const [planInfo, setPlanInfo] = useState({
    selectedPlan: 'professional',
    userCount: 3,
  });

  const [paymentInfo, setPaymentInfo] = useState({
    billingName: '',
    billingEmail: '',
    billingAddress: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
  });

  const [adminUser, setAdminUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  const selectedPlanData = PLANS?.find(p => p?.id === planInfo?.selectedPlan);
  const monthlyTotal = selectedPlanData ? selectedPlanData?.pricePerUser * planInfo?.userCount : 0;

  const validateStep = () => {
    setError('');
    if (currentStep === 1) {
      if (!companyInfo?.companyName || !companyInfo?.companyEmail || !companyInfo?.industry) {
        setError('Please fill in Company Name, Email, and Industry.');
        return false;
      }
    }
    if (currentStep === 3) {
      if (!paymentInfo?.billingName || !paymentInfo?.billingEmail) {
        setError('Please fill in billing name and email.');
        return false;
      }
    }
    if (currentStep === 4) {
      if (!adminUser?.firstName || !adminUser?.lastName || !adminUser?.email || !adminUser?.password) {
        setError('Please fill in all required admin user fields.');
        return false;
      }
      if (adminUser?.password !== adminUser?.confirmPassword) {
        setError('Passwords do not match.');
        return false;
      }
      if (adminUser?.password?.length < 8) {
        setError('Password must be at least 8 characters.');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (currentStep < 4) setCurrentStep(prev => prev + 1);
    else handleSubmit();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Generate slug from company name
      const slug = companyInfo?.companyName?.toLowerCase()?.replace(/[^a-z0-9]+/g, '-')?.replace(/(^-|-$)/g, '') + '-' + Date.now();

      // Create organization
      const { data: org, error: orgError } = await supabase?.from('organizations')?.insert({
          name: companyInfo?.companyName,
          slug,
          email: companyInfo?.companyEmail,
          phone: companyInfo?.companyPhone,
          address_line1: companyInfo?.companyAddress,
          city: companyInfo?.companyCity,
          state: companyInfo?.companyState,
          zip_code: companyInfo?.companyZip,
          is_active: true,
        })?.select()?.single();

      if (orgError) throw orgError;

      // Create subscription
      const planData = PLANS?.find(p => p?.id === planInfo?.selectedPlan);
      await supabase?.from('org_subscriptions')?.insert({
        organization_id: org?.id,
        plan: planInfo?.selectedPlan,
        status: 'trialing',
        price_per_user_monthly: planData?.pricePerUser || 29,
        max_users: planData?.maxUsers || 25,
        current_user_count: planInfo?.userCount,
        billing_email: paymentInfo?.billingEmail,
        billing_name: paymentInfo?.billingName,
        billing_address: paymentInfo?.billingAddress,
      });

      // Create onboarding record
      await supabase?.from('org_onboarding')?.insert({
        organization_id: org?.id,
        status: 'completed',
        current_step: 4,
        company_name: companyInfo?.companyName,
        company_email: companyInfo?.companyEmail,
        company_phone: companyInfo?.companyPhone,
        selected_plan: planInfo?.selectedPlan,
        user_count: planInfo?.userCount,
        admin_first_name: adminUser?.firstName,
        admin_last_name: adminUser?.lastName,
        admin_email: adminUser?.email,
        admin_phone: adminUser?.phone,
        payment_completed: true,
        completed_at: new Date()?.toISOString(),
      });

      // Create admin member record
      await supabase?.from('org_members')?.insert({
        organization_id: org?.id,
        email: adminUser?.email,
        full_name: `${adminUser?.firstName} ${adminUser?.lastName}`,
        role: 'owner',
        is_active: true,
        joined_at: new Date()?.toISOString(),
      });

      setSuccess(true);
    } catch (err) {
      setError(err?.message || 'Failed to complete onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircle" size={40} color="var(--color-success)" />
          </div>
          <h1 className="text-3xl font-heading font-bold text-foreground mb-3">Welcome Aboard!</h1>
          <p className="text-muted-foreground mb-2">Your organization <strong>{companyInfo?.companyName}</strong> has been successfully set up.</p>
          <p className="text-muted-foreground mb-8">Your 14-day free trial has started. No credit card charged yet.</p>
          <div className="bg-card rounded-xl p-6 border border-border mb-6 text-left">
            <h3 className="font-semibold text-foreground mb-3">Setup Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Organization</span><span className="font-medium">{companyInfo?.companyName}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><span className="font-medium capitalize">{planInfo?.selectedPlan}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Users</span><span className="font-medium">{planInfo?.userCount}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Monthly Total</span><span className="font-medium text-primary">${monthlyTotal}/mo</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Admin</span><span className="font-medium">{adminUser?.firstName} {adminUser?.lastName}</span></div>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard')} className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:bg-primary/90 transition-smooth">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Organization Onboarding - RIGJOCKEY</title></Helmet>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Wrench" size={20} color="var(--color-primary)" />
          </div>
          <span className="text-lg font-heading font-semibold text-foreground">RIGJOCKEY</span>
          <span className="text-muted-foreground ml-2">— Organization Setup</span>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-10">
          {/* Step Indicator */}
          <div className="flex items-center justify-between mb-10">
            {STEPS?.map((step, idx) => (
              <React.Fragment key={step?.id}>
                <div className="flex flex-col items-center gap-2">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-smooth ${
                    currentStep > step?.id ? 'bg-success border-success' :
                    currentStep === step?.id ? 'bg-primary border-primary': 'bg-card border-border'
                  }`}>
                    {currentStep > step?.id
                      ? <Icon name="Check" size={18} color="white" />
                      : <Icon name={step?.icon} size={18} color={currentStep === step?.id ? 'white' : 'var(--color-muted-foreground)'} />
                    }
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${
                    currentStep === step?.id ? 'text-primary' : 'text-muted-foreground'
                  }`}>{step?.title}</span>
                </div>
                {idx < STEPS?.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    currentStep > step?.id ? 'bg-success' : 'bg-border'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step Content */}
          <div className="bg-card rounded-2xl border border-border p-8 shadow-elevation-1">
            {/* Step 1: Company Info */}
            {currentStep === 1 && (
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-1">Company Information</h2>
                <p className="text-muted-foreground mb-6">Tell us about your organization to get started.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-1">Company Name <span className="text-destructive">*</span></label>
                    <input type="text" value={companyInfo?.companyName} onChange={e => setCompanyInfo(p => ({...p, companyName: e?.target?.value}))} placeholder="Acme Field Services" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Business Email <span className="text-destructive">*</span></label>
                    <input type="email" value={companyInfo?.companyEmail} onChange={e => setCompanyInfo(p => ({...p, companyEmail: e?.target?.value}))} placeholder="info@company.com" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Phone Number</label>
                    <input type="tel" value={companyInfo?.companyPhone} onChange={e => setCompanyInfo(p => ({...p, companyPhone: e?.target?.value}))} placeholder="(555) 000-0000" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Industry <span className="text-destructive">*</span></label>
                    <select value={companyInfo?.industry} onChange={e => setCompanyInfo(p => ({...p, industry: e?.target?.value}))} className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
                      <option value="">Select industry...</option>
                      {INDUSTRIES?.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Company Size</label>
                    <select value={companyInfo?.companySize} onChange={e => setCompanyInfo(p => ({...p, companySize: e?.target?.value}))} className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
                      <option value="">Select size...</option>
                      {COMPANY_SIZES?.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-1">Street Address</label>
                    <input type="text" value={companyInfo?.companyAddress} onChange={e => setCompanyInfo(p => ({...p, companyAddress: e?.target?.value}))} placeholder="123 Main St" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">City</label>
                    <input type="text" value={companyInfo?.companyCity} onChange={e => setCompanyInfo(p => ({...p, companyCity: e?.target?.value}))} placeholder="Houston" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">State</label>
                      <input type="text" value={companyInfo?.companyState} onChange={e => setCompanyInfo(p => ({...p, companyState: e?.target?.value}))} placeholder="TX" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">ZIP</label>
                      <input type="text" value={companyInfo?.companyZip} onChange={e => setCompanyInfo(p => ({...p, companyZip: e?.target?.value}))} placeholder="77001" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Plan Selection */}
            {currentStep === 2 && (
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-1">Choose Your Plan</h2>
                <p className="text-muted-foreground mb-6">Select the plan that best fits your team size and needs.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {PLANS?.map(plan => (
                    <button key={plan?.id} onClick={() => setPlanInfo(p => ({...p, selectedPlan: plan?.id}))} className={`relative text-left p-5 rounded-xl border-2 transition-smooth ${
                      planInfo?.selectedPlan === plan?.id ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/40'
                    }`}>
                      {plan?.badge && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">{plan?.badge}</span>
                      )}
                      <div className="font-heading font-bold text-foreground text-lg mb-1">{plan?.name}</div>
                      <div className="text-2xl font-bold text-primary mb-1">${plan?.pricePerUser}<span className="text-sm font-normal text-muted-foreground">/user/mo</span></div>
                      <ul className="mt-3 space-y-1">
                        {plan?.features?.map(f => (
                          <li key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Icon name="Check" size={12} color="var(--color-success)" />{f}
                          </li>
                        ))}
                      </ul>
                    </button>
                  ))}
                </div>
                <div className="bg-muted/50 rounded-xl p-5">
                  <label className="block text-sm font-medium text-foreground mb-3">Number of Users: <span className="text-primary font-bold">{planInfo?.userCount}</span></label>
                  <input type="range" min={1} max={selectedPlanData?.maxUsers === 999 ? 100 : selectedPlanData?.maxUsers || 25} value={planInfo?.userCount} onChange={e => setPlanInfo(p => ({...p, userCount: parseInt(e?.target?.value)}))} className="w-full accent-primary" />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1"><span>1</span><span>{selectedPlanData?.maxUsers === 999 ? '100+' : selectedPlanData?.maxUsers}</span></div>
                  <div className="mt-4 pt-4 border-t border-border flex justify-between items-center">
                    <span className="text-muted-foreground">Estimated Monthly Total</span>
                    <span className="text-2xl font-bold text-primary">${monthlyTotal}<span className="text-sm font-normal text-muted-foreground">/mo</span></span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">14-day free trial included. No credit card charged until trial ends.</p>
                </div>
              </div>
            )}

            {/* Step 3: Payment Setup */}
            {currentStep === 3 && (
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-1">Payment Setup</h2>
                <p className="text-muted-foreground mb-6">Your card will not be charged until your 14-day trial ends.</p>
                <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6 flex items-center gap-3">
                  <Icon name="Shield" size={20} color="var(--color-success)" />
                  <div>
                    <p className="text-sm font-medium text-foreground">14-Day Free Trial</p>
                    <p className="text-xs text-muted-foreground">No charges until {new Date(Date.now() + 14 * 86400000)?.toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Billing Name <span className="text-destructive">*</span></label>
                    <input type="text" value={paymentInfo?.billingName} onChange={e => setPaymentInfo(p => ({...p, billingName: e?.target?.value}))} placeholder="John Smith" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Billing Email <span className="text-destructive">*</span></label>
                    <input type="email" value={paymentInfo?.billingEmail} onChange={e => setPaymentInfo(p => ({...p, billingEmail: e?.target?.value}))} placeholder="billing@company.com" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-1">Billing Address</label>
                    <input type="text" value={paymentInfo?.billingAddress} onChange={e => setPaymentInfo(p => ({...p, billingAddress: e?.target?.value}))} placeholder="123 Main St, Houston, TX 77001" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-1">Card Number</label>
                    <div className="relative">
                      <input type="text" value={paymentInfo?.cardNumber} onChange={e => setPaymentInfo(p => ({...p, cardNumber: e?.target?.value}))} placeholder="4242 4242 4242 4242" maxLength={19} className="w-full px-3 py-2 pl-10 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      <Icon name="CreditCard" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Expiry Date</label>
                    <input type="text" value={paymentInfo?.cardExpiry} onChange={e => setPaymentInfo(p => ({...p, cardExpiry: e?.target?.value}))} placeholder="MM/YY" maxLength={5} className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">CVC</label>
                    <input type="text" value={paymentInfo?.cardCvc} onChange={e => setPaymentInfo(p => ({...p, cardCvc: e?.target?.value}))} placeholder="123" maxLength={4} className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-border">
                  <div className="flex justify-between text-sm mb-1"><span className="text-muted-foreground">Plan</span><span className="font-medium capitalize">{planInfo?.selectedPlan}</span></div>
                  <div className="flex justify-between text-sm mb-1"><span className="text-muted-foreground">Users</span><span className="font-medium">{planInfo?.userCount} × ${selectedPlanData?.pricePerUser}/mo</span></div>
                  <div className="flex justify-between text-base font-bold mt-2"><span>Monthly Total (after trial)</span><span className="text-primary">${monthlyTotal}/mo</span></div>
                </div>
              </div>
            )}

            {/* Step 4: Create Admin User */}
            {currentStep === 4 && (
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-1">Create Admin Account</h2>
                <p className="text-muted-foreground mb-6">Set up the primary administrator account for your organization.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">First Name <span className="text-destructive">*</span></label>
                    <input type="text" value={adminUser?.firstName} onChange={e => setAdminUser(p => ({...p, firstName: e?.target?.value}))} placeholder="John" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Last Name <span className="text-destructive">*</span></label>
                    <input type="text" value={adminUser?.lastName} onChange={e => setAdminUser(p => ({...p, lastName: e?.target?.value}))} placeholder="Smith" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Email Address <span className="text-destructive">*</span></label>
                    <input type="email" value={adminUser?.email} onChange={e => setAdminUser(p => ({...p, email: e?.target?.value}))} placeholder="admin@company.com" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Phone Number</label>
                    <input type="tel" value={adminUser?.phone} onChange={e => setAdminUser(p => ({...p, phone: e?.target?.value}))} placeholder="(555) 000-0000" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Password <span className="text-destructive">*</span></label>
                    <input type="password" value={adminUser?.password} onChange={e => setAdminUser(p => ({...p, password: e?.target?.value}))} placeholder="Min. 8 characters" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Confirm Password <span className="text-destructive">*</span></label>
                    <input type="password" value={adminUser?.confirmPassword} onChange={e => setAdminUser(p => ({...p, confirmPassword: e?.target?.value}))} placeholder="Repeat password" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                </div>
                <div className="mt-5 bg-muted/50 rounded-lg p-4">
                  <p className="text-sm font-medium text-foreground mb-2">Organization Summary</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Company: </span><span className="font-medium">{companyInfo?.companyName}</span></div>
                    <div><span className="text-muted-foreground">Plan: </span><span className="font-medium capitalize">{planInfo?.selectedPlan}</span></div>
                    <div><span className="text-muted-foreground">Users: </span><span className="font-medium">{planInfo?.userCount}</span></div>
                    <div><span className="text-muted-foreground">Monthly: </span><span className="font-medium text-primary">${monthlyTotal}/mo</span></div>
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
                <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-border">
              <button
                onClick={() => currentStep > 1 ? setCurrentStep(p => p - 1) : navigate('/dashboard')}
                className="px-6 py-2.5 border border-border rounded-lg font-medium text-foreground hover:bg-muted transition-smooth"
              >
                {currentStep === 1 ? 'Cancel' : 'Back'}
              </button>
              <button
                onClick={handleNext}
                disabled={loading}
                className="px-8 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-smooth disabled:opacity-50 flex items-center gap-2"
              >
                {loading && <Icon name="Loader2" size={16} color="white" className="animate-spin" />}
                {currentStep === 4 ? 'Complete Setup' : 'Continue'}
                {!loading && currentStep < 4 && <Icon name="ArrowRight" size={16} color="white" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrganizationOnboarding;
